"use client";
"use no memo";

import { useLazyQuery } from "@apollo/client/react";
import type { Renderable, SortingState, Table } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { VariableSizeList as List } from "react-window";

import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { Spinner } from "@/components/shared/spinner";
import UserMultipleSelect from "@/components/shared/users-search/user-multiple-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useFocusMarketData } from "@/contexts/focus-market-data";
import { UserContext } from "@/contexts/user-context";
import type { TokenBalanceSummary } from "@/graphql/codegen/graphql";
import {
  HoldersByPublicKeyDocument,
  TokenBalanceSummariesOrderBy,
} from "@/graphql/codegen/graphql";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import type { PaginationRequestParams } from "@/hooks-api/use-pagination-request";
import { usePaginationRequest } from "@/hooks-api/use-pagination-request";
import { ACCOUNT_USERNAMES_WITH_DISABLED_FOCUS_MARKET } from "@/utils/constants";
import { getVisiblePrice } from "@/utils/currency";
import { cn } from "@/utils/lib";
import { getOrderByKey } from "@/utils/trade";
import type { PartialAccount } from "@/utils/types";

import { tableColumns } from "./holders-table-columns";
import { TableItem } from "./holders-table-item";

interface WalletCreatorCoinHoldingsProps {
  publicKey: string;
  height?: number;
  className?: string;
  showHoldersSearch?: boolean;
}

type TableParams = {
  id: string;
  column: { columnDef: { cell: Renderable<object> } };
  getContext: () => { row: { id: string }; column: { id: string } };
};

const ITEMS_PER_PAGE = 20;

const HoldersTable = ({
  publicKey,
  height = 500,
  className,
  showHoldersSearch = false,
}: WalletCreatorCoinHoldingsProps) => {
  const { data: coinPropertiesData } = useCoinPropertiesQuery({
    publicKey,
  });

  const [holders, setHolders] = useState<Array<TokenBalanceSummary>>([]);

  const [totalCount, setTotalCount] = useState(0);
  const [columnSorting, setColumnSorting] = useState<SortingState>([]);
  const { focusExchangeRate, loading: loadingMarketData } =
    useFocusMarketData();
  const hasMoreHolders = totalCount > holders.length;
  const [holdersPercentageMap, setHoldersPercentageMap] = useState<
    Record<string, number>
  >({});
  const IsLoggedIn = useIsLoggedIn();
  const [skipPagination, setSkipPagination] = useState(false);
  const [selectedHolders, setSelectedHolders] = useState<PartialAccount[]>([]);
  const { currentUserPublicKey } = use(UserContext);
  const requestVersionRef = useRef(0);

  const hasCoinPriceInQuoteCurrency =
    !!(
      coinPropertiesData?.coinPrice &&
      getVisiblePrice(coinPropertiesData.coinPrice)
    ) &&
    !ACCOUNT_USERNAMES_WITH_DISABLED_FOCUS_MARKET.includes(
      coinPropertiesData.coinProperties.Profile.Username.toLowerCase(),
    );

  const [fetchHolders] = useLazyQuery(HoldersByPublicKeyDocument, {
    fetchPolicy: "network-only",
  });

  const columns = useMemo(
    () =>
      tableColumns({
        holdersPercentageMap,
        currentUserPublicKey,
        hasCoinPriceInQuoteCurrency,
        focusExchangeRate,
        coinPropertiesData,
        isLoggedIn: IsLoggedIn,
      }),
    [
      holdersPercentageMap,
      currentUserPublicKey,
      hasCoinPriceInQuoteCurrency,
      focusExchangeRate,
      coinPropertiesData,
      IsLoggedIn,
    ],
  );

  const getHolders = useCallback(
    async (params?: PaginationRequestParams) => {
      if (!publicKey) return;

      const requestVersion =
        typeof params?.requestVersion === "number"
          ? params.requestVersion
          : requestVersionRef.current;
      const offset = params?.changeParams ? 0 : holders.length;
      const selectedHoldersPublicKeys = selectedHolders.map(
        (holder) => holder.publicKey ?? "",
      );

      const request = fetchHolders({
        variables: {
          first: ITEMS_PER_PAGE,
          offset,
          publicKey,
          includeTokenCreator: selectedHoldersPublicKeys.length === 0,
          orderBy: getOrderByKey(
            columnSorting,
            TokenBalanceSummariesOrderBy,
            TokenBalanceSummariesOrderBy.TotalBalanceDesc,
          ),
          filter: {
            hodler: {
              publicKey: {
                notEqualTo: publicKey,
                in:
                  selectedHoldersPublicKeys.length > 0
                    ? selectedHoldersPublicKeys
                    : null,
              },
            },
          },
          hodlerBalanceFilter: {
            hodler: {
              publicKey: {
                equalTo: publicKey,
              },
            },
          },
        },
      });
      request.retain?.();
      const { data } = await request;

      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      const {
        tokenBalanceSummariesAsCreator,
        hodlerBalance,
        tokenAggAsCreator,
      } = data?.accountByPublicKey || {};

      const totalCount =
        (tokenBalanceSummariesAsCreator?.totalCount ?? 0) +
        (hodlerBalance?.totalCount ?? 0);

      const newHolders = [
        ...(!offset ? (hodlerBalance?.nodes.filter(Boolean) ?? []) : []),
        ...(tokenBalanceSummariesAsCreator?.nodes.filter(Boolean) ?? []),
      ];

      const totalCoinNanos = Number(tokenAggAsCreator?.totalBalanceNanos ?? 0);

      const newHoldersPercentageMap = newHolders.reduce((acc, holder) => {
        const balanceNanos = Number(holder?.totalBalance);
        const percentage = (balanceNanos / totalCoinNanos) * 100;
        const holderPublicKey = holder?.hodler?.publicKey;

        if (!holderPublicKey) return acc;

        return {
          ...acc,
          [holderPublicKey]: percentage,
        };
      }, {});

      const finalMap = { ...holdersPercentageMap, ...newHoldersPercentageMap };

      setHoldersPercentageMap(finalMap as Record<string, number>);
      setTotalCount(totalCount);

      setHolders((oldList) =>
        params?.changeParams
          ? (newHolders as TokenBalanceSummary[])
          : ([...oldList, ...newHolders] as TokenBalanceSummary[]),
      );
    },
    [
      publicKey,
      fetchHolders,
      holders.length,
      columnSorting,
      holdersPercentageMap,
      selectedHolders,
    ],
  );

  const COLUMNS_WIDTH = hasCoinPriceInQuoteCurrency
    ? [6, 34, 16, 20, 18, 6]
    : [6, 40, 24, 24, 6];

  // TanStack Table exposes methods React Compiler cannot memoize safely.
  // This table is intentionally opted out of compiler memoization.
  const table = useReactTable({
    data: holders,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setColumnSorting,
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    state: {
      sorting: columnSorting,
    },
  });

  const { ref, loadingMore, initialLoading, setInitialLoading } =
    usePaginationRequest({
      initialRequest: getHolders,
      fetchRequest: getHolders,
      hasMoreItems: hasMoreHolders,
      skipRequest: skipPagination,
    });

  const run = useEffectEvent(() => {
    requestVersionRef.current += 1;
    const requestVersion = requestVersionRef.current;
    setSkipPagination(true);
    void getHolders({ changeParams: true, requestVersion }).finally(() => {
      setSkipPagination(false);
    });
  });

  useEffect(() => {
    if (columnSorting.length) {
      run();
    }
  }, [columnSorting]);

  useEffect(() => {
    run();
  }, [selectedHolders]);

  useEffect(() => {
    requestVersionRef.current += 1;
    setInitialLoading(true);
  }, [publicKey, setInitialLoading]);

  const tableRows = table.getRowModel().rows;

  return (
    <>
      {showHoldersSearch && (
        <div className="mb-2 flex justify-end">
          <UserMultipleSelect
            buttonClassName="w-[220px]"
            users={selectedHolders}
            onSelectedUsers={setSelectedHolders}
            placeholder="Search holders..."
          />
        </div>
      )}

      <div className={cn("w-full text-muted", className)}>
        {initialLoading || loadingMarketData ? (
          <div className="px-2 pb-2">
            <Skeleton className="mt-4 h-[40px] w-full" />
            <Skeleton className="mt-4 h-[280px] w-full" />
          </div>
        ) : (
          <div className="w-full overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <div
                key={headerGroup.id}
                className="flex w-full min-w-[600px] border-b"
              >
                {headerGroup.headers.map((header, idx) => {
                  return (
                    <div
                      className="
                        flex h-11 items-center px-2 text-left text-xs
                        font-medium text-muted-foreground
                      "
                      key={header.id}
                      style={{ width: `${COLUMNS_WIDTH[idx]}%` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </div>
                  );
                })}
              </div>
            ))}
            {tableRows?.length ? (
              <List
                width="100%"
                height={height}
                itemCount={tableRows.length}
                itemSize={() => 68}
                itemData={{
                  table: table as unknown as Table<TableParams>,
                  tableRows,
                  columnsWidth: COLUMNS_WIDTH,
                  renderLoader: () =>
                    ((!initialLoading && holders.length !== totalCount) ||
                      loadingMore) && (
                      <div key="load-more-row" ref={ref}>
                        <div
                          className="
                            flex items-center justify-center gap-x-2 py-2
                          "
                        >
                          <Spinner />
                          <span>Loading...</span>
                        </div>
                      </div>
                    ),
                }}
                style={{
                  minWidth: 600,
                }}
              >
                {TableItem}
              </List>
            ) : (
              <div className="h-24 text-center">No holders found.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HoldersTable;
